import React from 'react';
import { Modal, TextInput, Loader, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { formatMoney } from '../../../../utils/money';
import { useZCreditTransactionsController } from './useZCreditTransactionsController';
import type { ZCreditTransactionsModalProps } from './zcredit-transactions.types';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './zcredit-transactions.module.scss';
import type { ZCreditReportDeal } from './zcredit-transactions.types';

const formatOccurredAt = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('he-IL');
};

const dealTypeLabel = (status: ZCreditReportDeal['status']): { text: string; className: string } => {
  if (status === 'approved') {
    return { text: 'חיוב', className: classes.dealTypeCharge };
  }
  if (status === 'reversed') {
    return { text: 'זיכוי', className: classes.dealTypeRefund };
  }
  if (status === 'declined' || status === 'cancelled') {
    return { text: 'נכשל', className: classes.dealTypeCancelled };
  }
  return { text: 'לא ידוע', className: classes.dealTypeUnknown };
};

// Summary: Manager view of a closed session's raw ZCredit terminal transactions, local amount search only.
const ZCreditTransactionsModalComponent: React.FC<ZCreditTransactionsModalProps> = ({
  opened,
  onClose,
  onBack,
  tenantId,
  sessionId,
  sessionName,
  employeeSession,
}) => {
  const c = useZCreditTransactionsController({ opened, tenantId, sessionId, employeeSession });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <span className={classes.titleRow}>
          <UnstyledButton type="button" onClick={onBack} className={classes.backButton} aria-label="חזרה לבחירת משמרת" data-testid="zcredit-transactions-back">
            <FontAwesomeIcon icon={faArrowRight as IconProp} />
          </UnstyledButton>
          <span className={classes.title}>{sessionName ? `עסקאות אשראי - ${sessionName}` : 'עסקאות אשראי'}</span>
        </span>
      }
      size="xl"
      centered
      radius="md"
      dir="rtl"
      classNames={{ root: navyModal.root }}
      overlayProps={{ opacity: 0.55, blur: 2 }}
      closeButtonProps={{ 'data-testid': 'zcredit-transactions-close' } as Record<string, string>}
    >
      <div className={classes.shell}>
        <div className={classes.searchRow}>
          <TextInput
            data-testid="zcredit-transactions-search"
            label="חיפוש לפי סכום"
            placeholder="לדוגמה 55.50"
            value={c.amountSearch}
            onChange={(event) => c.setAmountSearch(event.currentTarget.value)}
            radius="md"
            style={{ flex: 1 }}
          />
        </div>
        <div className={classes.warning}>
          <span className={classes.warningIcon}>{'⚠️'}</span>
          <span>
            ייתכנו אי־התאמות בארבע הספרות האחרונות של כרטיס האשראי המוצגות, עקב מנגנוני הסוואת כרטיס הנהוגים בשירותי
            תשלום דיגיטליים (כגון Apple Pay ו-Google Pay).
          </span>
        </div>

        {c.isLoading ? (
          <div className={classes.statusArea}>
            <Loader size="sm" />
          </div>
        ) : c.error ? (
          <p className={classes.error}>{c.error}</p>
        ) : c.filteredDeals.length === 0 ? (
          <p className={classes.empty}>לא נמצאו עסקאות</p>
        ) : (
          <div className={classes.list}>
            {c.filteredDeals.map((deal, index) => {
              const dealType = dealTypeLabel(deal.status);
              return (
                <div key={deal.transactionId ?? `${deal.approvalNumber ?? ''}-${index}`} className={classes.row} data-testid={`zcredit-transactions-row-${deal.transactionId ?? index}`}>
                  <span className={classes.rowMain}>
                    <span className={`${classes.dealTypeBadge} ${dealType.className}`}>{dealType.text}</span>
                    <span className={classes.rowCard}>{deal.cardLast4 ? `${deal.cardLast4} ••••` : '—'}</span>
                  </span>
                  <span className={classes.rowSide}>
                    <span className={classes.rowAmount}>{deal.amount !== null ? `${formatMoney(deal.amount)} ₪` : '—'}</span>
                    <span className={classes.rowDate}>{formatOccurredAt(deal.occurredAt)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export const ZCreditTransactionsModal = React.memo(ZCreditTransactionsModalComponent);
